import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { PencilSimple } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { Trash } from "@phosphor-icons/react/dist/ssr/Trash";
import { Eye } from "@phosphor-icons/react/dist/ssr/Eye";
import { ChatCircle } from "@phosphor-icons/react/dist/ssr/ChatCircle";
import { Users } from "@phosphor-icons/react/dist/ssr/Users";
import { Clock } from "@phosphor-icons/react/dist/ssr/Clock";
import { formatTimeAgo } from "../../../utils/date";

interface Thread {
  id: string;
  title: string;
  created: string;
  lastModified?: string;
  posts?: {
    id: string;
    message: string;
    depth: number;
    created: string;
    author: {
      id: string;
      name: string;
    };
  }[];
}

interface AdminDiscussionCardProps {
  thread: Thread;
  onEdit: (thread: Thread) => void;
  onDelete: (threadId: string, title: string) => void;
  onView: (thread: Thread) => void;
}

const AdminDiscussionCard: React.FC<AdminDiscussionCardProps> = ({ thread, onEdit, onDelete, onView }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const stripHtmlTags = (html: string) => {
    if (!html) return "";
    const withoutTags = html.replace(/<[^>]*>/g, "");
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = withoutTags;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const handleEdit = () => {
    onEdit(thread);
  };

  const handleDelete = () => {
    onDelete(thread.id, thread.title);
  };

  const handleView = () => {
    onView(thread);
  };

  const handleReadMoreToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Find the initial post (depth 0)
  const initialPost = thread.posts?.find((post) => post.depth === 0);
  const discussionContent = initialPost?.message ? stripHtmlTags(initialPost.message) : "";
  const shouldShowReadMore = discussionContent.length > 150;

  // Calculate stats
  const replyCount = (thread.posts?.length || 1) - 1; // Subtract 1 for the initial post
  const participantCount = new Set(thread.posts?.map((post) => post.author?.id) || []).size;
  const lastActivity = thread.lastModified || thread.created;

  // Get author from initial post
  const author = initialPost?.author?.name || "Unknown Author";

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {/* Top Row: Title on left, Edit/Delete buttons on right */}
        <View style={styles.topRow}>
          <CustomText text={thread.title} size="m" weight="bold" style={styles.title} useTranslationText={false} />
          <View style={styles.actionButtons}>
            <TouchableOpacity onPress={handleEdit} style={styles.iconButton}>
              <PencilSimple size={18} weight="bold" color="#6B7280" />
            </TouchableOpacity>
            <TouchableOpacity onPress={handleDelete} style={styles.iconButton}>
              <Trash size={18} weight="bold" color="#EF4444" />
            </TouchableOpacity>
          </View>
        </View>

        <CustomSpacing type="vertical" size="s" />

        {/* Author and Created Date Row */}
        <View style={styles.metaRow}>
          <CustomText text={`by ${author}`} size="s" style={styles.author} useTranslationText={false} />
          <CustomText
            text={`Created ${formatDate(thread.created)}`}
            size="s"
            style={styles.date}
            useTranslationText={false}
          />
        </View>

        <CustomSpacing type="vertical" size="m" />

        {/* Discussion Content */}
        {discussionContent && (
          <>
            <CustomText
              text={isExpanded || !shouldShowReadMore ? discussionContent : `${discussionContent.substring(0, 150)}...`}
              size="s"
              style={styles.discussionContent}
              useTranslationText={false}
            />

            {shouldShowReadMore && (
              <>
                <CustomSpacing type="vertical" size="s" />
                <TouchableOpacity onPress={handleReadMoreToggle} style={styles.readMoreButton}>
                  <CustomText
                    text={isExpanded ? "Read Less" : "Read More"}
                    size="s"
                    style={styles.readMoreText}
                    useTranslationText={false}
                  />
                </TouchableOpacity>
              </>
            )}

            <CustomSpacing type="vertical" size="m" />
          </>
        )}

        {/* Stats Row */}
        <View style={styles.statsRow}>
          <View style={styles.stat}>
            <ChatCircle size={12} color="#667085" />
            <CustomText text={`${replyCount} replies`} size="s" style={styles.statText} useTranslationText={false} />
          </View>
          <View style={styles.stat}>
            <Users size={12} color="#667085" />
            <CustomText
              text={`${participantCount} participants`}
              size="s"
              style={styles.statText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.stat}>
            <Clock size={12} color="#667085" />
            <CustomText
              text={`Last activity ${formatTimeAgo(lastActivity)}`}
              size="s"
              style={styles.statText}
              useTranslationText={false}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="m" />

        {/* Separator Line */}
        <View style={styles.separator} />

        <CustomSpacing type="vertical" size="m" />

        {/* View Discussion Button */}
        <CustomButton
          text="View Discussion"
          styleType="primary"
          size="m"
          leftIcon={<Eye size={20} weight="bold" color="#FFFFFF" />}
          onPress={handleView}
          style={styles.viewButton}
          useTranslationText={false}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
  },
  content: {
    padding: 20,
  },
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  title: {
    color: "#1E293B",
    flex: 1,
    marginRight: 16,
  },
  actionButtons: {
    flexDirection: "row",
    gap: 8,
  },
  iconButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  author: {
    color: "#64748B",
  },
  date: {
    color: "#64748B",
  },
  discussionContent: {
    color: "#374151",
    lineHeight: 21,
  },
  readMoreButton: {
    alignSelf: "flex-start",
  },
  readMoreText: {
    color: "#3E68FE",
    fontWeight: "600",
  },
  statsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 20,
    flexWrap: "wrap",
  },
  stat: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  statText: {
    color: "#6B7280",
  },
  separator: {
    height: 1,
    backgroundColor: "#E5E7EB",
    width: "100%",
  },
  viewButton: {
    alignSelf: "flex-start",
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
});

export default AdminDiscussionCard;
